import type { RateCardRowVM } from "./view-model";
// Marketplace M6-A: pure billing helpers (card-expired derivation + rate-card
// copy/formatting). Zero React/Stripe deps so this stays locally-testable
// without the @stripe runtime — see frontend/pro CLAUDE.md fe-payment-element
// notes on mocking the @stripe/react-stripe-js boundary.

/**
 * A card is expired once the current date is past the END of its expiration
 * month (matches Stripe/issuer convention — a card is valid through the last
 * day of its exp month).
 */
export function isCardExpired(
  expMonth: number,
  expYear: number,
  now: Date = new Date(),
): boolean {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  return (
    currentYear > expYear ||
    (currentYear === expYear && currentMonth > expMonth)
  );
}

/** Formats a card expiry as zero-padded MM/YY. */
export function formatCardExpiry(expMonth: number, expYear: number): string {
  return `${String(expMonth).padStart(2, "0")}/${String(expYear).slice(-2)}`;
}

/** Formats a fee in cents as customer-facing copy ("Free" for zero). */
export function formatFeeCents(cents: number): string {
  return cents === 0 ? "Free" : `$${(cents / 100).toFixed(2)}`;
}

/**
 * The buyer tiers an agent can hold a standing offer at. The catalog
 * (migration 119) also prices T4 (pre-approved, lender-owned), but no
 * composer tier exists for it, so no pro surface should quote it.
 */
export const STANDING_OFFER_TIERS = ["t1", "t2", "t3"] as const;
export type StandingOfferTier = (typeof STANDING_OFFER_TIERS)[number];

/** Marketplace-cause rows on a standing-offer tier (retention and T4 dropped). */
function standingMarketplaceRows(
  rows: readonly RateCardRowVM[] | null | undefined,
): Array<RateCardRowVM & { tier: StandingOfferTier }> {
  return (rows ?? []).filter(
    (row): row is RateCardRowVM & { tier: StandingOfferTier } =>
      row.cause === "marketplace" &&
      (STANDING_OFFER_TIERS as readonly string[]).includes(row.tier),
  );
}

/**
 * Per-composer-tier formatted NET marketplace connection fee, from the pro's
 * own rate card. Retention rows ($0, own invited client) and T4 are ignored;
 * a tier with no row is simply absent so a caller renders nothing for it
 * rather than a placeholder price.
 */
export function rateCardFeeByTier(
  rows: readonly RateCardRowVM[] | null | undefined,
): Partial<Record<StandingOfferTier, string>> {
  const out: Partial<Record<StandingOfferTier, string>> = {};
  for (const row of standingMarketplaceRows(rows)) {
    out[row.tier] = formatFeeCents(row.netCents);
  }
  return out;
}

/**
 * True only when the pro's own rate card carries a labelled grant on a
 * standing-offer tier that fully waives the fee (net 0 on a non-zero list),
 * i.e. an unused promo the backend will apply to their next connection. A
 * partial discount (e.g. an admin "25% off") is not "free". Fail-closed: an unloaded or errored card (null/undefined)
 * is false. Never computed from config.
 */
export function hasUnusedPromo(
  rows: readonly RateCardRowVM[] | null | undefined,
): boolean {
  return standingMarketplaceRows(rows).some(
    (row) =>
      !!row.grantLabel &&
      row.netCents === 0 &&
      row.listCents > 0,
  );
}

/** Rate-card uniform-pricing copy (task:fe-rate-card-table). */
export const RATE_CARD_UNIFORM_COPY =
  "Base rates are the same for every agent at a tier. Account-specific promotions may lower your rate. No fee when you connect with your own invited client.";

/** Formats a rate-card tier code for display (e.g. "t1" → "T1"). */
export function formatTier(tier: string): string {
  return tier.toUpperCase();
}

export type FeePromo = { listCents: number; netCents: number; label: string };

/**
 * The promo presentation of a backend fee quote: present only when a labelled
 * grant actually lowered the fee. The figures are the quote's own (list from
 * `list_cents`, net from `net_cents`); this never computes a price.
 */
export function feeQuotePromo(quote: {
  list_cents: number;
  discount_cents: number;
  net_cents: number;
  grant_label?: string;
}): FeePromo | null {
  if (!quote.grant_label || quote.discount_cents <= 0) return null;
  return {
    listCents: quote.list_cents,
    netCents: quote.net_cents,
    label: quote.grant_label,
  };
}

/**
 * Per-composer-tier promo presentation from the pro's own rate card: present
 * only for a marketplace row a labelled grant lowered.
 */
export function rateCardPromoByTier(
  rows: readonly RateCardRowVM[] | null | undefined,
): Partial<Record<StandingOfferTier, FeePromo>> {
  const out: Partial<Record<StandingOfferTier, FeePromo>> = {};
  for (const row of standingMarketplaceRows(rows)) {
    if (!row.grantLabel || row.netCents >= row.listCents) continue;
    out[row.tier] = {
      listCents: row.listCents,
      netCents: row.netCents,
      label: row.grantLabel,
    };
  }
  return out;
}
